import React, { useEffect } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import { EventType } from "@azure/msal-browser";
import { jwtDecode } from "jwt-decode";
import { b2cPolicies, protectedResources } from "./authConfig";
import { compareIssuingPolicy } from "./utils/claimUtils";

import Dashboard from "./containers/Dashboard";
import DataConsole from "./containers/Dataconsole/";
import DataSet from "./containers/Dataconsole/";
import AiAssistant from "./containers/AiAssistant/";
import ExternalUser from "./containers/AiAssistant/ExternalUser"; // ✅ External user route
import NotFound from "./components/NotFound";
import DataSets from "./containers/Datasets/";
import UserList from "./containers/Users/";
import AdminDatasetrequest from "./containers/AdminDataset/";
import GroupsPage from "./containers/GroupManagement";
import Organisations from "./containers/Organisations/";
import Settings from "./containers/Settings";
import Unauthorized from "./components/Unauthorized";
import DatasetDashboard from "./containers/DatasetDashboard";
import FeedbackDashboard from "./containers/FeedbackDashboard";
import EngagementDashboard from "./containers/EngagementDashboard";
import TokenUsageDashboard from "./containers/TokenUsageDashboard";
import SessionUsageDashboard from "./containers/SessionUsageDashboard";
import Configuration from "./containers/Configuration";

const AppRoutes = ({ isAuthenticated, userType, activeAccount, activeToken }) => {
  const { instance } = useMsal();
  const orgDetails = useSelector((state) => state.admin.details);

  let hasOrganizationAccess = false;
  let hasOrganizationDisable = false;

  // ✅ External user bypass check
  const isExternalUser = localStorage.getItem("isExternalUser") === "true";
  
  if (isExternalUser) {
   
    return (
      <Routes>
        <Route
          path="/aiassistant"
          element={<ExternalUser />}
        />
        {/* external user sees only aiassistant, all other paths → NotFound */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    );
  }

  // MSAL Events (keep as is for internal users)
  useEffect(() => {
    const callbackId = instance.addEventCallback((event) => {
      
      try {
        if (
          (event.eventType === EventType.LOGIN_SUCCESS ||
            event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
          event.payload.account
        ) {
        
          if (
            compareIssuingPolicy(
              event.payload.idTokenClaims,
              b2cPolicies.names.editProfile
            )
          ) {
            const originalSignInAccount = instance
              .getAllAccounts()
              ?.find(
                (account) =>
                  account.idTokenClaims.oid ===
                    event.payload.idTokenClaims.oid &&
                  account.idTokenClaims.sub ===
                    event.payload.idTokenClaims.sub &&
                  compareIssuingPolicy(
                    account.idTokenClaims,
                    b2cPolicies.names.signUpSignIn
                  )
              );

            if (originalSignInAccount) {
              let signUpSignInFlowRequest = {
                authority: b2cPolicies.authorities.signUpSignIn.authority,
                account: originalSignInAccount,
              };

              instance.ssoSilent(signUpSignInFlowRequest);
            }
          }

          if (
            compareIssuingPolicy(
              event.payload.idTokenClaims,
              b2cPolicies.names.forgotPassword
            )
          ) {
            let signUpSignInFlowRequest = {
              authority: b2cPolicies.authorities.signUpSignIn.authority,
              scopes: [
                ...protectedResources.apiTodoList.scopes.read,
                ...protectedResources.apiTodoList.scopes.write,
              ],
            };
            instance.loginRedirect(signUpSignInFlowRequest);
          }
        }

        if (event.eventType === EventType.LOGIN_FAILURE) {
          console.warn("[MSAL] Login failure:", event.error);
          if (
            event.error &&
            event.error.errorMessage.includes("AADB2C90118")
          ) {
            const resetPasswordRequest = {
              authority: b2cPolicies.authorities.forgotPassword.authority,
              scopes: [],
            };
            instance.loginRedirect(resetPasswordRequest);
          }
        }
      } catch (error) {
        console.error("[MSAL CALLBACK ERROR]:", error);
        console.error("Error in event callback:", error);
      }
    });

    return () => {
      if (callbackId) instance.removeEventCallback(callbackId);
    };
  }, [instance]);

  // Org claim check
  const token = localStorage.getItem("token");
 
  if (token) {
    try {
      const decodedToken = jwtDecode(token);
      hasOrganizationAccess =
        decodedToken && decodedToken.extension_Organization;
      hasOrganizationDisable =
        orgDetails?.disabled != undefined ? orgDetails.disabled : false;
    } catch (error) {
      console.error("Error decoding token:", error);
    }
  }

  const showSidebar = activeAccount && activeToken;


  // 🔄 Normal MSAL-based routing (unchanged)
  return (
    <Routes>
      <Route path="/settings" element={<Settings showSidebar={showSidebar} />} />
      <Route
        path="/settings/privacy-policy"
        element={<Settings showSidebar={showSidebar} />}
      />
      <Route
        path="/settings/faq"
        element={<Settings showSidebar={showSidebar} />}
      />

      {/* Organization Settings (/configuration) */}
      {isAuthenticated &&
        hasOrganizationAccess &&
        !hasOrganizationDisable &&
        userType === "OrgAdmin" && (
          <Route path="/configuration" element={<Configuration />} />
        )}

      {isAuthenticated && hasOrganizationAccess && !hasOrganizationDisable ? (
        <>
          {isAuthenticated && userType === "ExternalUser" && (
            <Route
              path="/aiassistant"
              element={<AiAssistant title="AI Assistant" pageID="ai-assistant" />}
            />
          )}

          {isAuthenticated && userType === "User" && (
            <>
              <Route
                path="/"
                element={<AiAssistant title="AI Assistant" pageID="ai-assistant" />}
              />
              <Route
                path="/aiassistant"
                element={<AiAssistant title="AI Assistant" pageID="ai-assistant" />}
              />
              <Route
                path="/data-sets"
                element={<DataSets title="Datasets" pageID="data-sets" />}
              />
              <Route
                path="/other-datasets"
                element={<DataSets title="Datasets" pageID="other-datasets" />}
              />
            </>
          )}
          {isAuthenticated && userType === "HumanAgent" && (
            <>
              <Route
                path="/"
                element={<AiAssistant title="AI Assistant" pageID="ai-assistant" />}
              />
              <Route
                path="/aiassistant"
                element={<AiAssistant title="AI Assistant" pageID="ai-assistant" />}
              />
            </>
          )}
          { isAuthenticated && userType === "SuperAdmin" && (
            <>
              <Route
                path="/"
                element={
                  <Organisations
                    title="Organizations"
                    pageID="organisations_console"
                  />
                }
              />
              <Route
                path="/organizations"
                element={
                  <Organisations
                    pageID="organisations_console"
                    title="Organizations"
                  />
                }
              />
            </>
          )}
          {isAuthenticated && userType === "QuickChatUser" && (
            <>
              <Route
                path="/"
                element={<Navigate to="/aiassistant?quickChat=1&method=quickChat&loading=true" replace />}
              />
              <Route
                path="/aiassistant"
                element={
                  <AiAssistant
                    title="AI Assistant"
                    pageID="ai-assistant"
                    userType={userType}
                  />
                }
              />
            </>
          )}
          {isAuthenticated && (userType === "Admin" || userType === "OrgAdmin"||userType==="DatasetAdmin") && (
            <>
              <Route
                path="/"
                element={<DataSet pageID="data_console" title="Data Console" />}
              />
              <Route
                path="/adminconsole/allusers"
                element={<UserList pageID="all_users" title=" All users" />}
              />
              <Route
                path="/adminconsole/groups"
                element={<GroupsPage pageID="groups" title="Groups"  userType={userType} />}
              />
              <Route
                path="/adminconsole/requests"
                element={<AdminDatasetrequest pageID="dataset_request" title="Requests" />}
              />
              <Route
                path="/adminconsole/requests/:userIdParam/:userNameParam"
                element={<AdminDatasetrequest title="Dataset Requests" pageID="user_dataset_requests" />}
              />
              <Route
                path="/dataset/:datasetId/:datasetNameParam"
                element={<DataConsole title="Add Document" pageID="add_doc" userType={userType} />}
              />
              <Route
                path="/dataconsole"
                element={<DataSet pageID="data_console" title="Data Console" />}
              />
              <Route
                path="/aiassistant"
                element={<AiAssistant title="AI Assistant" pageID="ai-assistant" userType={userType} />}
              />
              <Route
                path="/dashboard/datasetdashboard"
                element={<DatasetDashboard title="Rating Dashboard" pageID="dataset_dashboard" />}
              />
              <Route
                path="/dashboard/orgdashboard"
                element={<Dashboard title="Organization Dashboard" pageID="org_dashboard" />}
              />
              <Route
                path="/dashboard/feedbackdashboard"
                element={<FeedbackDashboard title="Feedback Dashboard" pageID="feedback_dashboard" />}
              />
              <Route
                path="/dashboard/engagementdashboard"
                element={<EngagementDashboard title="User Engagement" pageID="engagement_dashboard" />}
              />
              {/* Token Usage - OrgAdmin only */}
              {userType === "OrgAdmin" && (
                <Route
                  path="/dashboard/tokenusage"
                  element={<TokenUsageDashboard title="Token Usage" pageID="token_usage_dashboard" />}
                />
              )}
              {/* Session Usage - OrgAdmin, Admin, and DatasetAdmin */}
              {(userType === "OrgAdmin" || userType === "Admin" || userType === "DatasetAdmin") && (
                <Route
                  path="/dashboard/sessionusage"
                  element={<SessionUsageDashboard title="Session Usage" pageID="session_usage_dashboard" />}
                />
              )}
              
              <Route path="/settings" element={<Settings showSidebar={showSidebar} />} />
              <Route path="/settings/privacy-policy" element={<Settings showSidebar={showSidebar} />} />
              <Route path="/settings/faq" element={<Settings showSidebar={showSidebar} />} />
              

            </>
          )}
        </>
      ) : (
        <Route path="*" element={<Unauthorized />} />
      )}
      <Route path="/callback" element={<Navigate to="/aiassistant" replace />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;
